import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Award, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api/client";
import { getTheme } from "@/lib/theme";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Account() {
  const { user, signOut, selectedBusinessId } = useAuth();
  const nav = useNavigate();
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    if (user?.role === "customer") api.getLoyalty().then(setLoyalty).catch(() => {});
  }, [user]);

  const del = async () => {
    try {
      await api.deleteAccount();
      signOut();
      toast.success("Account deleted");
      nav("/shop");
    } catch (err) {
      toast.error(err.message || "Could not delete account");
    }
  };

  if (!user) return null;

  return (
    <div className="container-app py-8 sm:py-12 max-w-2xl">
      <h1 className="font-display text-4xl" style={{ color: "var(--ink)" }}>Account</h1>

      <div className="mt-6 rounded-2xl p-6" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full flex items-center justify-center font-display text-2xl" style={{ background: "var(--accent-soft)", color: "var(--brand)" }}>
            {user.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <div className="font-display text-xl" style={{ color: "var(--ink)" }}>{user.name}</div>
            <div className="text-sm" style={{ color: "var(--muted)" }}>{user.email}</div>
            <div className="text-xs uppercase tracking-wide mt-0.5" style={{ color: "var(--brand)" }}>{user.role}</div>
          </div>
        </div>
      </div>

      {user.role === "customer" && loyalty && (
        <div className="mt-5 rounded-2xl p-6" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2 mb-3">
            <Award style={{ color: "var(--accent)" }} size={20} />
            <h2 className="font-display text-xl" style={{ color: "var(--ink)" }}>Loyalty stamps</h2>
          </div>
          <div className="space-y-3">
            {Object.values(loyalty.by_business).map((b) => {
              const t = getTheme(b.business_id);
              const pct = Math.min(100, (b.stamps / b.stamps_per_reward) * 100);
              return (
                <div key={b.business_id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold" style={{ color: "var(--ink)" }}>{t.name}</span>
                    <span style={{ color: "var(--muted)" }}>{b.stamps}/{b.stamps_per_reward}</span>
                  </div>
                  <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "var(--surface-2)" }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--brand)" }} />
                  </div>
                  {b.active_vouchers?.length > 0 && (
                    <div className="text-xs mt-1 font-semibold" style={{ color: "#12633a" }}>
                      {b.active_vouchers.length} reward voucher{b.active_vouchers.length > 1 ? "s" : ""} available!
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-5 flex items-center justify-between gap-3">
        <button onClick={() => { signOut(); nav("/shop"); }} className="text-sm font-semibold underline" style={{ color: "var(--muted)" }}>
          Sign out
        </button>
        {user.role === "customer" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="delete-account-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600">
                <Trash2 size={15} /> Delete account
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                <AlertDialogDescription>This can't be undone. Your order history will no longer be accessible.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}
